import {Suspense, ReactNode} from 'react';
import {View, StyleSheet} from 'react-native';
import {useIsFetching, useQueryClient} from '@tanstack/react-query';
import {appDataKeys} from '@/data/app-data-keys';
import {DashboardSkeleton} from './DashboardSkeleton';

interface SuspenseWithRetryFallbackProps {
    children: ReactNode;
    fallback?: ReactNode;
}

/**
 * Enhanced Suspense boundary that shows fallback during TanStack Query retries
 * when there's no cached data available.
 *
 * Regular Suspense doesn't retrigger when queries retry after failures,
 * leaving users with a blank screen. This component monitors the global
 * fetching state and shows the fallback during retries ONLY if there's no data yet.
 *
 * If data already exists in cache (background refetch/retry), the skeleton is NOT shown.
 */
export const SuspenseWithRetryFallback = ({
    children,
    fallback = <DashboardSkeleton />,
}: SuspenseWithRetryFallbackProps) => {
    const queryClient = useQueryClient();

    // Monitor if any queries are currently fetching (including retries)
    const isFetching = useIsFetching();

    // Check if we have cached data for the main app data query
    const cachedData = queryClient.getQueryData(appDataKeys.detail());
    const hasData = cachedData !== undefined;

    // Only show loading overlay if:
    // 1. Queries are fetching (including retries)
    // 2. AND there's no cached data yet (initial load/retry scenario)
    const shouldShowLoading = isFetching > 0 && !hasData;

    return (
        <Suspense fallback={fallback}>
            {shouldShowLoading ? (
                <View style={styles.overlay}>
                    {fallback}
                </View>
            ) : (
                children
            )}
        </Suspense>
    );
};

const styles = StyleSheet.create({
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
    },
});
